import { useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { isStandaloneDisplayMode } from '../helpers/isStandaloneDisplayMode';
import InstallAppModal from '../InstallAppModal';

type InstallAppBannerProps = { t: any };
const DISMISSED_KEY = 'dined-install-banner-dismissed';

const wasDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

export default function InstallAppBanner({ t }: InstallAppBannerProps) {
  const [visible, setVisible] = useState(() => !isStandaloneDisplayMode() && !wasDismissed());
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const displayMode = window.matchMedia('(display-mode: standalone)');
    const hideWhenInstalled = () => {
      if (isStandaloneDisplayMode()) setVisible(false);
    };

    displayMode.addEventListener?.('change', hideWhenInstalled);
    window.addEventListener('appinstalled', hideWhenInstalled);
    return () => {
      displayMode.removeEventListener?.('change', hideWhenInstalled);
      window.removeEventListener('appinstalled', hideWhenInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // Dismissal still works when browser storage is unavailable.
    }
  };

  if (!visible) return null;

  return (
    <>
      <aside className="install-app-banner" aria-label={t.getTheApp}>
        <button type="button" className="install-app-banner-main" onClick={() => setModalOpen(true)}>
          <span className="install-app-banner-icon" aria-hidden="true">
            <img src="/dined-mark.svg" alt="" />
          </span>
          <span className="install-app-banner-copy">
            <small>{t.installAppEyebrow}</small>
            <b>{t.getTheApp}</b>
          </span>
          <span className="install-app-banner-cta" aria-hidden="true">
            {t.installAppCta}
          </span>
        </button>
        <button type="button" className="install-app-banner-close" onClick={dismiss} aria-label={t.cancel}>
          <X weight="bold" />
        </button>
      </aside>
      {modalOpen && <InstallAppModal t={t} onClose={() => setModalOpen(false)} />}
    </>
  );
}
